import React, { useEffect, useRef, useState } from "react";
import { db } from "../services/firebase";
import firebase from "firebase/app";
import "firebase/firestore";

import { useCollectionData, useDocumentData } from "react-firebase-hooks/firestore";
import { Icon, Button } from "react-materialize";

import "../css/realtimechat.css";
import { inverseColorHSL, randomColorHSL } from "../helpers/Types.js";
import { openaiAddTagsMessage, openaiGetReplyMessage } from "../helpers/openai.js";

const MAX_CHARS_USERNAME = 60;
const MAX_CHARS_MESSAGES = 160;
const MAX_MESSAGES_PER_PAGE = 250;

export default function RealtimeChat(props) {
  const container = useRef();
  const dummy = useRef();

  const params = new URLSearchParams(window.location.search);
  let showForm = params.get('form') == null ? true : params.get('form') == "true";

  const [userScrolled, setUserScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [personalMessageIds, setPersonalMessageIds] = useState(getMyMessages());
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [awaitingForAIReplyId, setAwaitingForAIReplyId] = useState(null);

  const tagsQuery = db.collection("params").doc("messageTags");
  const [tags, loadingTags] = useDocumentData(tagsQuery);

  const categoriesQuery = db.collection("params").doc("messageCategories");
  const [categories, loadingCategories] = useDocumentData(categoriesQuery);

  useEffect(() => {
    function handleVisibilityChange() {
      setIsPageVisible(!document.hidden);
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!container.current) return;
    function onScroll() {
      const atBottom =
        container.current.scrollTop + container.current.clientHeight >=
        container.current.scrollHeight - 10;
      setUserScrolled(!atBottom);
    }
    container.current.addEventListener("scroll", onScroll);
    return () => container.current.removeEventListener("scroll", onScroll);
  }, []);

  const messagesRealtimeQuery = db
    .collection("realtime")
    .where("isDeleted", "==", false)
    .where("replyingTo", "==", "")
    .orderBy("timestamp", "desc")
    .limit(MAX_MESSAGES_PER_PAGE * currentPage);

  const [messagesRealtime, loadingMessagesRealtime] = useCollectionData(messagesRealtimeQuery);

  const [messagesReplyRealtime, setMessagesReplyRealtime] = useState([]);
  const [loadingMessagesReplyRealtime, setLoadingMessagesReplyRealtime] = useState(true);

  useEffect(() => {
    if (!messagesRealtime) return;
    if (messagesRealtime.length === 0) {
      setMessagesReplyRealtime([]);
      setLoadingMessagesReplyRealtime(false);
      return;
    }
    setLoadingMessagesReplyRealtime(true);

    const ids = messagesRealtime.filter(m => m.id).map(m => m.id);
    const chunks = [];
    for (let i = 0; i < ids.length; i += 10) {
      chunks.push(ids.slice(i, i + 10));
    }

    Promise.all(
      chunks.map(chunk =>
        db
          .collection("realtime")
          .where("isDeleted", "==", false)
          .where("replyingTo", "in", chunk)
          .get()
      )
    ).then(snapshots => {
      const replies = [];
      snapshots.forEach(snap => {
        snap.forEach(doc => replies.push({ ...doc.data(), id: doc.id }));
      });
      setMessagesReplyRealtime(replies);
      setLoadingMessagesReplyRealtime(false);
    });
  }, [messagesRealtime]);

  function shuffleArray(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function reorderMessagesWithReplies(messages) {
    const reorderMessages = [...messages];
    for (let i = 0; i < reorderMessages.length; i++) {
      const msg = reorderMessages[i];
      if (msg.replyingTo) {
        const parentIdx = reorderMessages.findIndex(m => m.id === msg.replyingTo);
        reorderMessages.splice(i, 1);
        reorderMessages.splice(parentIdx + 1, 0, msg)
      }
    }
    return reorderMessages;
  }

  useEffect(() => {
    if (
      (!loadingMessagesRealtime && messagesRealtime) ||
      (!loadingMessagesReplyRealtime && messagesReplyRealtime)
    ) {
      let realtimeFiltered = [];
      if (messagesRealtime && messagesReplyRealtime) {
        realtimeFiltered = [...messagesRealtime, ...messagesReplyRealtime]
          .filter((message) => !personalMessageIds.includes(message.id))
          .filter((message) => !messages.map((m) => m.id).includes(message.id))
          .filter((message) => message.id);
      }
      const shuffled = shuffleArray([
        ...realtimeFiltered,
      ]);
      setMessages((prev) => {
        // const prevIds = new Set(prev.map(m => m.id));
        // const uniques = shuffled.filter(m => !prevIds.has(m.id));
        // const ordered = reorderMessagesWithReplies([...prev, ...uniques]);
        // return ordered;

        const all = [...prev, ...shuffled];
        const byId = new Map();
        all.forEach(msg => {
          if (msg && msg.id) byId.set(msg.id, msg);
        });
        const deduped = Array.from(byId.values());
        const ordered = reorderMessagesWithReplies(deduped);
        return ordered;
      });
    }
  }, [
    loadingMessagesRealtime,
    messagesRealtime,
    loadingMessagesReplyRealtime,
    messagesReplyRealtime,
  ]);

  useEffect(() => {
  if (!isPageVisible) return;

  let newAwaitingForAIReplyId = awaitingForAIReplyId;

  if (awaitingForAIReplyId) {
    const waitingMsgIndex = messages.findIndex((m) => m.id === awaitingForAIReplyId);
    if (waitingMsgIndex >= 0 && currentMessageIndex <= waitingMsgIndex) {
      setCurrentMessageIndex(waitingMsgIndex + 1);
      return;
    }
    const responseExists = messages.some(
      (m) =>
        m.replyingTo === awaitingForAIReplyId &&
        !personalMessageIds.includes(m.id) &&
        typeof m.replyingTo === "string" &&
        m.replyingTo.trim() !== ""
    );
    if (!responseExists) {
      return;
    }
    newAwaitingForAIReplyId = null;
  }

  if (messages.length > 0 && currentMessageIndex < messages.length) {
    const currentMsg = messages[currentMessageIndex];

    const isReply = currentMsg?.replyingTo && currentMsg.replyingTo !== "";
    const isPersonalReply = isReply && personalMessageIds.includes(currentMsg.id);

    const randomDelay = 
      currentMessageIndex === 0 
        ? 0 
        : (isPersonalReply ? 0 : isReply ? 250 : Math.floor(Math.random() * 2000) + 1000);

    const timeoutId = setTimeout(() => {
      if (awaitingForAIReplyId !== newAwaitingForAIReplyId) setAwaitingForAIReplyId(newAwaitingForAIReplyId);
      setCurrentMessageIndex((prev) => prev + 1);
    }, randomDelay);
    return () => clearTimeout(timeoutId);
  }
}, [messages, currentMessageIndex, isPageVisible, personalMessageIds, awaitingForAIReplyId]);


  useEffect(() => {
    if (currentMessageIndex >= messages.length - 2) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
    if (!userScrolled && container.current) {
      container.current.scrollTop = container.current.scrollHeight;
    }
  }, [currentMessageIndex, messages.length]);

  const color = useRef("#0df233");
  const colorAI = useRef("#f20df1");

  const [formValue, setFormValue] = useState("");
  const [usernameValue, setUsernameValue] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    if (replyingTo) {
      sendMessage();
    }
  }, [replyingTo]);

  const replyCallback = (msg) => {
    setReplyingTo(msg);
  };

  const trimMessage = (text, maxChars) => {
    let trimmed = text.slice(0, maxChars);
    return trimmed;
  };
    
  async function getMyIP() {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  }

  async function hasReachedDailyLimit(ip) {
    const startOfDay = new Date();
    startOfDay.setUTCHours(0, 0, 0, 0);

    const messagesRef = db.collection("realtime");
    const snapshot = await messagesRef
      .where("ip", "==", ip)
      .where("timestamp", ">=", firebase.firestore.Timestamp.fromDate(startOfDay))
      .get();

    return snapshot.size >= 2;
  }

  const sendMessage = async (e) => {
    e?.preventDefault();

    const ip = await getMyIP();
    if (await hasReachedDailyLimit(ip) && !replyingTo) {
      alert("You have reached the limit of 2 messages today.");
      setFormValue("");
      setUsernameValue("");
      return;
    }

    const newMessage = {
      Body: replyingTo ? replyingTo.message : formValue,
      ProfileName: replyingTo ? replyingTo.name : usernameValue,
      timestamp: firebase.firestore.Timestamp.now(),
      wasShown: false,
      isShowing: false,
      isDeleted: false,
      isCheckedByAnswerAI: replyingTo ? true : false,
      isRealTime: true,
      replyingTo: replyingTo ? replyingTo.id : "",
      ip: replyingTo ? "" : ip,
    };

    setFormValue("");
    setUsernameValue("");

    let docRef = await db.collection("realtime").add(newMessage);
    addToMyMessages(docRef.id);
    newMessage.id = docRef.id;
    if (replyingTo) {
      if (tags && tags.tags) {
        await openaiAddTagsMessage(docRef, newMessage.Body, tags.tags, "tags");
      }
      if (categories && categories.tags) {
        await openaiAddTagsMessage(docRef, newMessage.Body, categories.tags, "categories");
      }
    }

    setMessages((prev) => {
      const newMessages = [...prev];
      if (replyingTo) {
        const parentIdx = newMessages.findIndex(m => m.id === replyingTo.id);
        if (parentIdx !== -1) {
          newMessages.splice(parentIdx + 1, 0, newMessage);
        } else {
          newMessages.splice(currentMessageIndex + 1, 0, newMessage);
        }
      } else {
        newMessages.splice(currentMessageIndex + 1, 0, newMessage);
      }
      return newMessages;
    });
    await db.collection("realtime").doc(docRef.id).update({
      id: docRef.id,
    });
    if (container.current) container.current.scrollTop = container.current.scrollHeight;

    if (replyingTo) {
      setReplyingTo(null);
    } else {
      setAwaitingForAIReplyId(docRef.id);
      await generateReply(docRef, docRef.id, newMessage.Body);
    }
  };

  function getMyMessages() {
    // let myMessages = sessionStorage.getItem("myMessages");
    // if (myMessages) {
    //   return JSON.parse(myMessages);
    // } else {
    //   return [];
    // }
    return [];
  }

  async function generateReply(originalRef, originalMessageID, originalMessage) {
    const replyMessage = await openaiGetReplyMessage(originalMessage);

    switch (replyMessage) {
      case "Harmful":
      case "harmful":
      case `"Harmful"`:
      case `"harmful"`:
      case "Harmful.":
      case "harmful.":
      case `"Harmful."`:
      case `"harmful."`:
      case "Political":
      case "political":
      case `"Political"`:
      case `"political"`:
      case "Political.":
      case "political.":
      case `"Political."`:
      case `"political."`:
        await db.collection("realtime").doc(originalMessageID).update({
          isDeleted: true,
          isCheckedByAnswerAI: true,
        });
        setMessages((prev) => prev.filter((msg) => msg.id !== originalMessageID));
        setAwaitingForAIReplyId(null);
        break;
      case "Statement":
      case "statement":
      case `"Statement"`:
      case `"statement"`:
      case "Statement.":
      case "statement.":
      case `"Statement."`:
      case `"statement."`:
        if (tags && tags.tags) {
          await openaiAddTagsMessage(originalRef, originalMessage, tags.tags, "tags");
        }
        if (categories && categories.tags) {
          await openaiAddTagsMessage(originalRef, originalMessage, categories.tags, "categories");
        }
        await db.collection("realtime").doc(originalMessageID).update({
          isCheckedByAnswerAI: true,
        });
        setAwaitingForAIReplyId(null);
        break;
      default:
        if (tags && tags.tags) {
          await openaiAddTagsMessage(originalRef, originalMessage, tags.tags, "tags");
        }
        if (categories && categories.tags) {
          await openaiAddTagsMessage(originalRef, originalMessage, categories.tags, "categories");
        }
        await db.collection("realtime").doc(originalMessageID).update({
          isCheckedByAnswerAI: true,
        });
        setReplyingTo({
          message: replyMessage,
          name: "AI Messenger",
          id: originalMessageID
        });
        setTimeout(() => {
          setAwaitingForAIReplyId(null);
        }, 100);
        break;
    }
  }

  function addToMyMessages(toAdd) {
    // let myMessages = getMyMessages();
    // myMessages.push(toAdd);
    // sessionStorage.setItem("myMessages", JSON.stringify(myMessages));
    setPersonalMessageIds((prev) => [...prev, toAdd]);
  }

  return (
    <>
      <div className="realtimeContainer">
        <div ref={container} className="realtimeMessagesContainer">
          {(loadingTags || loadingCategories || loadingMessagesRealtime) && messages.length === 0 && (
            <div>loading...</div>
          )}
          <ul>
            {messages &&
              messages
                .slice(0, currentMessageIndex)
                .filter((message, index, self) =>
                  index === self.findIndex((m) => m.id === message.id)
                )
                .map((msg, i) =>
                  msg && msg.id ? (
                    <RenderMessage
                      key={msg.id}
                      message={msg}
                      myMessages={personalMessageIds}
                      color={color}
                      colorAI={colorAI}
                      // replyCallback={replyCallback}
                    />
                  ) : null
                )}
            {awaitingForAIReplyId && (
              <li className="thinkingMessage">
                <div className="thinkingDots">
                  Thinking
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </li>
            )}
          </ul>
          <span ref={dummy}></span>
        </div>

        {showForm &&
          <div
            className="realtimeInputContainer"
            style={{ backgroundColor: color.current }}
          >
            {/* {replyingTo && (
              <RenderMessageReply
                message={replyingTo}
                replyCallback={replyCallback}
              />
            )} */}
            <form className="realtimeInputForm">
              <div className="realtimeInputs">
                <input
                  id="realtimeInputName"
                  className="realtimeInput"
                  value={usernameValue}
                  onChange={(e) =>
                    setUsernameValue(
                      trimMessage(e.target.value, MAX_CHARS_USERNAME)
                    )
                  }
                  placeholder="Your Name"
                />
                <input
                  id="realtimeInputText"
                  className="realtimeInput"
                  value={formValue}
                  onChange={(e) =>
                    setFormValue(trimMessage(e.target.value, MAX_CHARS_MESSAGES))
                  }
                  placeholder="Message"
                />
              </div>
              <button
                className="realtimeSend"
                type="submit"
                onClick={sendMessage}
                disabled={!formValue || !usernameValue || awaitingForAIReplyId}
                style={{
                  cursor: !formValue || !usernameValue || awaitingForAIReplyId ? "not-allowed" : "initial",
                }}
              >
                <Icon>send</Icon>
              </button>
            </form>
          </div>
        }
      </div>
    </>
  );
}

// ----------- SUB-COMPONENTS -----------

function RenderMessageReply(props) {
  let messageClass = "z-depth-0 card realtimeMessage replyChatMessage ";
  if (
    Array.isArray(props.myMessages) &&
    props.myMessages.includes(props.message.id)
  ) {
    messageClass += "ownReplyChatMessage";
  }
  return (
    <>
      {props.message && (
        <li
          id={"realtimemessagereply-" + props.message.id}
           className={messageClass}
        >
          <div className="realtimeReplyCard-header valign-wrapper" style={{color: props.colorAI.current}}>
            {props.message.ProfileName}
          </div>
          {/* <Button
            className="btn-floating  grey right replyMessageButton"
            node="button"
            small
            tooltip="Cancel reply"
            waves="light"
            floating
            icon={<Icon>cancel</Icon>}
            onClick={() => props.replyCallback(null)}
          /> */}
          <div className="realtimeReplyCard-content white-text">
            {props.message.Body}
          </div>
        </li>
      )}
    </>
  );
}

function RenderRealtimeMessage(props) {
  let messageClass = "z-depth-0  card realtimeMessage ";
  if (
    Array.isArray(props.myMessages) &&
    props.myMessages.includes(props.message.id)
  ) {
    messageClass += "ownChatMessage";
  }
  return (
    <>
      <li id={"realtimemessage-" + props.message.id} className={messageClass}>
        <div className="realtimeCard-header valign-wrapper" style={{color: props.color.current}}>
          {props.message.ProfileName}
        </div>
        {/* <Button
          className="btn-floating  grey right replyMessageButton"
          node="button"
          small
          tooltip="Reply this message"
          waves="light"
          floating
          icon={<Icon>reply</Icon>}
          onClick={() => props.replyCallback(props.message)}
        /> */}
        <div className="realtimeCard-content white-text">
          {props.message.Body}
        </div>
      </li>
    </>
  );
}

function RenderBoardMessage(props) {
  return (
    <>
      <li
        id={"realtimeBoardMessage-" + props.message.id}
        className="z-depth-0  card realtimeMessage realtimeBoardMessage"
      >
        <div className="realtimeCard-header valign-wrapper">
          {props.message.displayName}
        </div>
        <div className="realtimeCard-content white-text">
          {props.message.content}
        </div>
      </li>
    </>
  );
}

function RenderMessage(props) {
  let isReply = !(props.message.replyingTo == "");
  return props.message.isRealTime ? (
    isReply ? <RenderMessageReply {...props} /> : <RenderRealtimeMessage {...props} />
  ) : (
    <RenderBoardMessage {...props} />
  );
}
